import { DosVisit } from "@/components/encounter/DosVisit";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { displayName } from "@/lib/patientFormat";
import { createPortal } from "react-dom";
import { ChevronDown, Check } from "lucide-react";
import { toast } from "sonner";
import { patientOf } from "@/lib/claimReadiness";
import { composeRead, provOf, provNpiOf, tovOf, posLine, matchLabel, GROUP_META, reviewFlags, createdStamp } from "@/lib/createClaimsModel";

const HAIR = "#E9E9EC", INK = "#141412", INDIGO = "#5856D6", GREY = "#6E6E6B";
const MONO = "ui-monospace,Menlo,monospace";
export const STAGE_DOT = { coding: "#9B9B97", blocked: "#FF3B30", review: "#FF9500", ready: INDIGO, generated: "#34C759" };
export const STAGE_WORD = { coding: "Coding", blocked: "Blocked", review: "Needs review", ready: "Ready to submit", generated: "Claim generated" };

export const CC_CSS = `
.cc-page{container-type:inline-size;container-name:ccpage;min-width:0}
.cc-page > *{max-width:1680px;margin-left:auto;margin-right:auto}
.cc-tbl{container-type:inline-size;container-name:cctbl}
.ccg{display:grid;gap:8px;align-items:start;grid-template-columns:28px minmax(0,1.5fr) minmax(0,.8fr) 150px minmax(0,.9fr) minmax(0,1fr) minmax(0,1.1fr) minmax(0,1.5fr) minmax(0,.7fr) minmax(0,1.2fr)}
.ccg.flat{grid-template-columns:28px minmax(0,1.5fr) minmax(0,.8fr) minmax(0,1.1fr) 150px minmax(0,.9fr) minmax(0,1fr) minmax(0,1.1fr) minmax(0,1.5fr) minmax(0,.7fr) minmax(0,1.2fr)}
.ccr{padding:12px 16px;--rowbg:#fff;background:var(--rowbg);position:relative;cursor:pointer;transition:transform .2s ease,box-shadow .2s ease,border-radius .2s ease}
.ccr:not(.open):hover{transform:translateY(-1px);box-shadow:0 0 0 1px #E9E9EC,0 6px 18px rgba(20,20,18,.09);border-radius:8px;z-index:5;border-bottom-color:transparent!important}
.cc-item:has(+ .cc-item > .ccr:not(.open):hover) > :last-child{border-bottom-color:transparent!important}
.cc-item{position:relative}
.cc-item.open::before{content:"";position:absolute;left:0;top:0;bottom:0;width:3px;background:${INK};z-index:6;pointer-events:none}
.cc-chev{width:24px;height:24px;margin-top:-2px;border-radius:7px;display:grid;place-items:center;color:${GREY};background:transparent;opacity:0;transition:opacity .12s ease,background-color .12s ease}
.cc-chev svg{transition:transform .2s ease}
.ccr:hover .cc-chev,.ccr:focus-within .cc-chev,.cc-chev:focus-visible{opacity:1}
.ccr.open .cc-chev{opacity:1;background:#fff;box-shadow:0 0 0 1px #E9E9EC;color:${INK}}
.ccr.open .cc-chev svg{transform:rotate(90deg)}
.ccr.open .cc-chev:hover{background:#F1F1F4}
.cc-blur{transition:filter .2s ease,opacity .2s ease}
.cc-focus .cc-blur{filter:blur(1.6px);opacity:.5}
.cc-focus .cc-blur:hover{filter:none;opacity:.9}
@media (hover:none){.cc-chev{opacity:1}}
@media (prefers-reduced-motion:reduce){.ccr,.cc-chev,.cc-chev svg,.cc-blur{transition:none}.ccr:not(.open):hover{transform:none}.cc-focus .cc-blur,.cc-focus .cc-blur:hover{filter:none;opacity:.45}}
.cch{padding:8px 16px}
.cch .rd{white-space:normal;line-height:1.25}
.ccr{font-size:13px;font-weight:400;color:${INK}}
.cc-v{font-size:13px;font-weight:400;line-height:1.4}
.cc-s{font-size:11.5px;font-weight:400;line-height:1.45;color:#6E6E6B;margin-top:3px}
.cc-mrn{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;display:inline-block;max-width:100%;vertical-align:bottom}
.cc-pay{font-size:13px;font-weight:400;line-height:1.4;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;overflow-wrap:normal;word-break:normal;hyphens:none}
.cch{font-size:10.5px!important;font-weight:600!important;letter-spacing:.06em!important;text-transform:uppercase;color:#6E6E6B!important}
.cc-btn{font-size:12.5px!important;font-weight:600!important;white-space:nowrap}
.cc-lnk{font-size:11.5px;font-weight:600;color:#4A4AC2}
.cc-pn{font-size:14px;font-weight:600;line-height:1.3;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;overflow-wrap:break-word;word-break:normal;max-width:100%}
.cc-pv{font-size:13px;font-weight:400;line-height:1.4;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;overflow-wrap:break-word;word-break:normal;hyphens:none}
.cc-bl{margin-top:6px;font-size:11.5px;font-weight:400;line-height:1.45;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.cc-exp{font-size:13px;font-weight:600;line-height:1.4;font-variant-numeric:tabular-nums;text-align:right}
.cc-cl{display:flex;align-items:baseline;gap:4px 6px;flex-wrap:wrap}
.cc-cl .cd{font-family:${MONO};font-size:11px;font-weight:600;color:${INK}}
.cc-cl .pt{font-family:${MONO};font-size:10px;color:#9B9B97;margin-right:1px;font-weight:400}
.cc-cpy{position:relative}
.cc-cpyb{position:absolute;top:-3px;right:0;width:24px;height:24px;border:0;border-radius:6px;background:var(--rowbg,#fff);box-shadow:-10px 0 8px var(--rowbg,#fff);color:${GREY};display:grid;place-items:center;padding:0;cursor:pointer;opacity:0;transition:opacity .12s,background-color .12s,color .12s;z-index:2}
.cc-cpy:hover .cc-cpyb,.cc-cpyb:focus-visible{opacity:1;outline:none}
.ccr .cc-cpyb:hover{background:#EFEFF2;color:${INK}}
.cc-cpyb.done{opacity:1;color:#248A3D}
@media (hover:none){.cc-cpyb{opacity:.6}}
@container cctbl (min-width:911px){
  .ccr{padding:18px 20px 18px 14px}
  .cch{padding-left:14px;padding-right:20px}
  .cc-cov{display:none!important}
  .cc-stat .lbl{white-space:nowrap}
  .cc-cl{flex-wrap:nowrap;white-space:nowrap}
  .ccg.flat .cc-src{display:none!important}
  .ccg,.ccg.flat{column-gap:8px}
  .ccg.flat{grid-template-columns:28px minmax(104px,1.2fr) 116px 150px minmax(76px,.7fr) minmax(90px,1fr) minmax(120px,1fr) 56px 104px}
  .ccg{grid-template-columns:28px minmax(104px,1.2fr) 80px 150px minmax(76px,.7fr) minmax(96px,1fr) minmax(150px,1fr) 56px 104px}
  .cc-act{grid-column:auto;flex-direction:column!important;align-items:flex-start!important;gap:8px!important}
}
@container cctbl (min-width:1071px){
  .ccg,.ccg.flat{column-gap:12px}
  .ccg:not(.flat){grid-template-columns:28px minmax(100px,1.15fr) 84px 172px minmax(84px,.75fr) minmax(124px,1fr) minmax(180px,1fr) 80px 120px}
}
@container cctbl (min-width:1110px){
  .ccg.flat .cc-src{display:flex!important}
  .ccg.flat{column-gap:10px;grid-template-columns:28px minmax(118px,1.15fr) 70px 120px 172px minmax(78px,.75fr) minmax(96px,1fr) minmax(144px,1fr) 56px 124px}
}
@container cctbl (min-width:1200px){
  .ccg.flat{column-gap:10px}
  .ccg.flat{grid-template-columns:28px minmax(118px,1.15fr) 84px 120px 172px minmax(84px,.75fr) minmax(120px,1fr) minmax(176px,1fr) 70px 120px}
}
@container cctbl (min-width:1300px){
  .ccg,.ccg.flat{column-gap:12px}
  .cc-cov{display:block!important}
  .ccg.flat .cc-cov{display:none!important}
  .ccg:not(.flat){grid-template-columns:34px minmax(150px,1.3fr) 88px 172px minmax(90px,.8fr) minmax(130px,1fr) minmax(100px,1fr) minmax(190px,1.2fr) 88px 130px}
}
@container cctbl (min-width:1400px){
  .ccr{padding:20px 24px 20px 16px}
  .cch{padding-left:16px;padding-right:24px}
  .ccg,.ccg.flat{column-gap:18px}
}
@container cctbl (min-width:1450px){
  .ccg.flat .cc-cov{display:block!important}
  .ccg.flat{column-gap:18px;grid-template-columns:34px minmax(150px,1.3fr) 88px 124px 172px minmax(90px,.8fr) minmax(130px,1fr) minmax(90px,1fr) minmax(170px,1.2fr) 88px 130px}
}
@container cctbl (min-width:1561px){
  .ccg.flat{column-gap:22px;grid-template-columns:28px minmax(150px,1.9fr) 88px 124px 172px minmax(90px,.9fr) minmax(140px,1.25fr) minmax(120px,1.2fr) minmax(210px,1.3fr) 80px 130px}
  .ccg:not(.flat){grid-template-columns:28px minmax(150px,1.9fr) 88px 172px minmax(90px,.9fr) minmax(150px,1.25fr) minmax(120px,1.2fr) minmax(210px,1.3fr) 80px 140px}
}
.cc-srch{width:38px;height:38px;flex:none;border:1px solid ${HAIR};border-radius:10px;background:#fff;color:#6C6C69;display:inline-flex;align-items:center;justify-content:center;padding:0;overflow:hidden;cursor:pointer;transition:width .18s ease,background-color .15s}
.cc-srch:hover{background:#F6F6F8}
.cc-srch svg{width:14px;height:14px;stroke:currentColor;fill:none;stroke-width:1.7;flex:none}
.cc-srch.open{width:210px;justify-content:flex-start;gap:7px;padding:0 8px 0 11px;cursor:text}
.cc-srch.open:focus-within{border-color:${INDIGO}}
.cc-srch input{border:0;outline:0;background:none;width:100%;font-size:12.5px;color:${INK};min-width:0}
.cc-srch .csx{border:0;background:none;padding:0 2px;color:${GREY};font-size:16px;line-height:1;flex:none;cursor:pointer}
.cc-srch:not(.open) input,.cc-srch:not(.open) .csx{display:none}
.cc-rtrig{cursor:default;border-radius:3px;outline:none}
.cc-rtrig:hover,.cc-rtrig:focus-visible{color:${INK}}
.cc-tip{position:relative}
.cc-tip .tt{display:none;position:absolute;left:0;top:calc(100% + 4px);z-index:30;white-space:nowrap;background:${INK};color:#fff;font-size:11px;font-weight:500;padding:5px 8px;border-radius:7px}
.cc-tip:hover .tt,.cc-tip:focus .tt,.cc-tip:focus-within .tt{display:block}
.cc-tip .tt.wrap{white-space:normal;width:max-content;max-width:300px;line-height:1.4}
.cc-tovauto{}
.cc-tip:hover .cc-tovauto,.cc-tip:focus-within .cc-tovauto{}
.cc-stat .lbl{display:flex;align-items:center;gap:6px;font-size:13px;font-weight:600;line-height:1.4}
.cc-stat .lbl i{width:7px;height:7px;border-radius:999px;flex:none}
.cc-stat .why{margin-left:13px;font-size:11.5px;font-weight:400;line-height:1.45;margin-top:3px;color:#6E6E6B}
.cc-sig{margin-top:8px;margin-left:13px;display:flex;flex-direction:column;gap:5px;font-size:11.5px;font-weight:400;line-height:1.45}
.cc-sig .ln{display:flex;flex-wrap:wrap;align-items:baseline;column-gap:8px;row-gap:0}
.cc-sig .ok{position:relative}
.cc-sig .ok svg{position:absolute;left:-13px;top:2px}
.cc-sigalt .cc-sig .ok{padding-left:15px}.cc-sigalt .cc-sig .ok svg{left:0}
.cc-sig .lk{background:none;border:0;padding:0;font-size:11.5px;font-weight:600;color:#4A4AC2;cursor:pointer}
.cc-sig .lk:hover{color:#3A3AB0}
.cc-sig .unsign{color:#6E6E6B;font-weight:400;opacity:0;transition:opacity .12s}
.cc-sig:hover .unsign,.cc-sig .unsign:focus-visible{opacity:1}
@media (hover:none){.cc-sig .unsign{opacity:1}}
.cc-sigalt{display:none}.cc-sigalt.show{display:block}.cc-sigalt .cc-sig{margin-left:0}
.cc-stagebar{flex:none;max-width:100%}
.cc-needsyou-lnk{transition:background-color .12s}.cc-needsyou-lnk:hover{background:rgba(255,149,0,.12)}
.cc-refresh{transition:background-color .12s}.cc-refresh:hover{background:#F1F1F4}
.cc-scruball{transition:background-color .12s}.cc-scruball:hover:not(:disabled){background:#2A2A27!important}
@container ccpage (max-width:600px){.cc-head{flex-direction:column;align-items:stretch;gap:12px}.cc-headright{width:100%;justify-content:space-between}}
.cc-tov-flash{animation:ccTovFlash .9s ease-out}
@keyframes ccTovFlash{0%{background:rgba(88,86,214,.16)}100%{background:transparent}}
@media (max-width:720px){.cc-fcols{flex-direction:column}.cc-fleft{width:100%!important;border-right:none!important;border-bottom:1px solid ${HAIR}}.cc-fpanel{width:calc(100vw - 32px)!important}}
.cc-fgrid{display:grid;grid-template-columns:1fr 1fr;gap:0 24px;align-content:start}
.cc-fgrid .cc-fcell{border-top:1px solid ${HAIR}}
.cc-fgrid .cc-fcell:nth-child(1),.cc-fgrid .cc-fcell:nth-child(2){border-top:none}
@media (max-width:860px){.cc-fgrid{grid-template-columns:1fr}.cc-fgrid .cc-fcell{border-top:1px solid ${HAIR}}.cc-fgrid .cc-fcell:first-child{border-top:none}}
.cc-chip{display:inline-flex;align-items:center;gap:6px;height:24px;padding:0 8px;border-radius:6px;background:#EFEFF2;font-size:12.5px;line-height:1;white-space:nowrap}
.cc-chip-x{display:inline-flex;align-items:center;justify-content:center;width:16px;height:16px;color:#6E6E6B;background:none;border:0;padding:0;margin-right:-2px;cursor:pointer;border-radius:4px;transition:background-color .12s,color .12s}
.cc-chip-x:hover{color:#141412;background:rgba(20,20,18,.08)}
@container cctbl (max-width:910px){
  .cc-src,.cc-cov{display:none!important}
  .ccg{grid-template-columns:28px minmax(0,1.5fr) 150px minmax(0,.9fr) minmax(0,1fr) minmax(0,1.5fr) minmax(0,.7fr) minmax(116px,1.2fr)}
  .ccg.flat{grid-template-columns:28px minmax(0,1.5fr) minmax(0,1.1fr) 150px minmax(0,.9fr) minmax(0,1fr) minmax(0,1.5fr) minmax(0,.7fr) minmax(116px,1.2fr)}
}
/* ── Coverage / Source fold into the Patient cell — a column may leave the grid,
   but its data never leaves the row. These lines mirror EXACTLY the widths where
   .cc-cov / .cc-src are display:none above, so the data shows in one place only. ── */
.cc-fold-cov,.cc-fold-src{display:none}
@container cctbl (max-width:910px){
  .cc-fold-cov,.cc-fold-src{display:revert}
}
@container cctbl (911px <= width < 1300px){
  .ccg:not(.flat) .cc-fold-cov{display:revert}
}
@container cctbl (911px <= width < 1450px){
  .ccg.flat .cc-fold-cov{display:revert}
}
@container cctbl (911px <= width < 1110px){
  .ccg.flat .cc-fold-src{display:revert}
}
@container ccpage (max-width:720px){
  .cc-prov,.cc-stat,.cc-tovc{display:none!important}
  .cc-sigalt{display:block!important}
  .ccg,.ccg.flat{grid-template-columns:28px minmax(0,1.5fr) minmax(0,.85fr) minmax(0,1.5fr) minmax(0,.7fr)}
  .cc-act{grid-column:2 / -1;flex-direction:row!important;align-items:center!important;gap:12px!important}
  .cc-acth{display:none}
  .cc-stagebar{overflow-x:auto;flex-wrap:nowrap!important}
  .cc-stagebar > button{flex:none}
}
`;

// Sign / codes-out-of-date / signed block (Status column, or under the patient name when Status is hidden).
export function SigBlock({ n, onAskSign, onAskUnsign, onRecode, recoding, signing, alt }) {
  const signed = n.signed === true, busy = signing === n.id, p = alt ? "cc-sigalt-" : "cc-";
  return (
    <div className="cc-sig" data-testid={`${p}sig-${n.id}`}>
      {!signed ? (
        <div className="ln"><span style={{ color: "#9A5A00", fontWeight: 500 }} data-testid={`${p}notsigned-${n.id}`}>Not signed</span>
          <button className="lk" disabled={busy} onClick={(ev) => onAskSign(n, ev.currentTarget)} data-testid={`${p}sign-${n.id}`}>{busy ? "Signing…" : "Sign visit"}</button></div>
      ) : null}
      {n.codes_stale ? (
        recoding === n.id
          ? <div className="ln"><span style={{ color: "#6E6E6B" }} data-testid={`${p}recoding-${n.id}`}>Recoding…</span></div>
          : <div className="ln"><span style={{ color: "#9A5A00", fontWeight: 500 }} data-testid={`${p}stale-${n.id}`}>Codes out of date</span>
            <button className="lk" onClick={() => onRecode(n)} data-testid={`${p}recode-${n.id}`}>Recode</button></div>
      ) : null}
      {signed ? (
        <div className="ln"><span className="ok" style={{ color: "#6E6E6B" }} title={n.signed_at ? `Signed ${new Date(n.signed_at).toLocaleString()}${n.signed_by?.name ? ` · ${n.signed_by.name}` : ""}` : "Signed"} data-testid={`${p}signed-${n.id}`}>
          <Check size={11} strokeWidth={3} style={{ color: "#1F8A4C" }} />Signed</span>
          <button className="lk unsign" disabled={busy} onClick={(ev) => onAskUnsign(n, ev.currentTarget)} data-testid={`${p}unsign-${n.id}`}>{busy ? "Removing…" : "Unsign"}</button></div>
      ) : null}
    </div>
  );
}

// Sign / Unsign confirm popover, anchored to the link that opened it.
export function SignConfirm({ c, onCancel, onConfirm, z }) {
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) onCancel(); };
    const onKey = (e) => { if (e.key === "Escape") onCancel(); };
    document.addEventListener("mousedown", onDoc); document.addEventListener("keydown", onKey);
    ref.current?.querySelector(c.kind === "sign" ? "[data-yes]" : "[data-no]")?.focus();
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [c, onCancel]);
  const name = patientOf(c.n), W = 300;
  const left = Math.max(8, Math.min(c.rect.left, window.innerWidth - W - 8));
  const pos = c.rect.bottom + 190 > window.innerHeight ? { left, bottom: window.innerHeight - c.rect.top + 6 } : { left, top: c.rect.bottom + 6 };
  const body = c.kind === "sign"
    ? "Signing records your name and the time, and lets this encounter's claim be generated. A signature can be removed later."
    : `${name}'s encounter goes back to Not signed, and its claim can't be ${c.n.claim_status === "queued" ? "saved again" : "generated"} until it's re-signed.`;
  return createPortal(
    <div ref={ref} role="dialog" className="fixed z-[90] bg-white rounded-[12px]" style={{ ...pos, ...(z ? { zIndex: z } : {}), width: W, padding: "14px 16px", border: `1px solid ${HAIR}`, boxShadow: "0 16px 44px rgba(0,0,0,.16)" }} data-testid="cc-sign-confirm">
      <b className="block text-[14px] mb-1" style={{ color: INK }} data-testid="cc-sign-confirm-title">{c.kind === "sign" ? `Sign ${name}'s encounter?` : "Remove the signature?"}</b>
      <p className="text-[13px] mb-3" style={{ color: "#6C6C69" }} data-testid="cc-sign-confirm-body">{body}</p>
      <div className="flex justify-end gap-2">
        <button data-no onClick={onCancel} className="text-[13px] font-semibold rounded-[8px] px-3 h-[32px] bg-white" style={{ border: `1px solid ${HAIR}`, color: INK }} data-testid="cc-sign-confirm-cancel">Cancel</button>
        <button data-yes onClick={onConfirm} className="text-[13px] font-semibold rounded-[8px] px-3 h-[32px] text-white" style={{ background: INK }} data-testid="cc-sign-confirm-yes">{c.kind === "sign" ? "Sign" : "Remove signature"}</button>
      </div>
    </div>, document.body);
}

const COPY_SVG = <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M7 9.667a2.667 2.667 0 0 1 2.667 -2.667h8.666a2.667 2.667 0 0 1 2.667 2.667v8.666a2.667 2.667 0 0 1 -2.667 2.667h-8.666a2.667 2.667 0 0 1 -2.667 -2.667z" /><path d="M4.012 16.737a2.005 2.005 0 0 1 -1.012 -1.737v-10c0 -1.1 .9 -2 2 -2h10c.75 0 1.158 .385 1.5 1" /></svg>;

// Hover copy: overlaid at the cell's top-right; never toggles the row.
export function CopyBtn({ text, what, testid }) {
  const [done, setDone] = useState(false);
  const go = (ev) => {
    ev.stopPropagation();
    navigator.clipboard?.writeText(text).then(() => { setDone(true); toast.success(`Copied ${what}`); setTimeout(() => setDone(false), 1200); }).catch(() => toast.error("Couldn't copy. Your browser blocked the clipboard."));
  };
  return <button type="button" className={`cc-cpyb ${done ? "done" : ""}`} onClick={go} aria-label={`Copy ${what}`} data-testid={testid}>{done ? <Check size={14} strokeWidth={2.5} /> : COPY_SVG}</button>;
}

// ", M.D." → ",\u00A0M.D." so a credential never sits alone on a line.
export const bindCred = (s) => String(s || "").replace(/,\s+([^,\s]{1,8})$/, ",\u00A0$1");

const MATCH_OK = new Set(["matched", "preselected", "confirmed"]);
const matchProblem = (n) => !MATCH_OK.has(String(n?.match_status || ""));

export function PatientCell({ n, onOpen, onOpenChart, onMatch, sigAlt, sigShow, foldSrc, foldCov }) {
  const pos = posLine(n);
  const name = patientOf(n);
  const missing = name === "Unnamed patient";
  const hasChart = !!n.chart_uuid;
  const openName = () => (hasChart && onOpenChart ? onOpenChart(n) : onOpen(n));
  return (
    <div className="cc-cpy min-w-0">
      <button onClick={openName} title={missing ? undefined : name} className={`text-left hover:underline ${missing ? "text-[14px] font-normal" : "cc-pn"}`} style={{ color: missing ? GREY : INK }} data-testid={`cc-open-${n.id}`}>{missing ? "Name missing" : name}</button>
      {missing ? null : <CopyBtn text={name} what="patient name" testid={`cc-copy-patient-${n.id}`} />}
      <div className="cc-s" data-testid={`cc-match-${n.id}`}><span className="cc-mrn">MRN {n.mrn || "—"}</span>{matchProblem(n) ? <> · {onMatch ? <button onClick={(e) => { e.stopPropagation(); onMatch(n); }} className="hover:underline" style={{ color: "#9A5A00", fontWeight: 600 }} data-testid={`cc-match-fix-${n.id}`}>{matchLabel(n)} · match patient</button> : <span style={{ color: "#9A5A00", fontWeight: 500 }} data-testid={`cc-match-warn-${n.id}`}>{matchLabel(n)}</span>}</> : null}</div>
      {pos ? <div className="cc-s" data-testid={`cc-pos-${n.id}`}>{pos}{foldSrc ? <span className="cc-fold-src"> · {foldSrc}</span> : null}</div> : null}
      {foldSrc && !pos ? <div className="cc-s cc-fold-src" data-testid={`cc-fold-src-${n.id}`}>{foldSrc}</div> : null}
      {foldCov ? <div className="cc-s cc-fold-cov" data-testid={`cc-fold-cov-${n.id}`}>{foldCov}</div> : null}
      {sigAlt ? <div className={`cc-sigalt ${sigShow ? "show" : ""}`}>{sigAlt}</div> : null}
    </div>
  );
}

export function DosCell({ n }) {
  return <DosVisit n={n} tid="cc" noDos={<span className="inline-flex items-center text-[11.5px] font-medium" style={{ color: "#C4302A" }} title="Date of service missing — set it to bill this encounter" data-testid={`cc-nodos-${n.id}`}>Needs DOS</span>} />;
}

// Visit type column — value as returned (plain text, no emblem). Blank / "NOT SET" → faint "Not set ⌄"
// (read-only "Not set" with no chevron when claim_status === "queued"). Picker: search + custom + keys.
export function TovCell({ n, tovList, onSet }) {
  const tov = tovOf(n);
  const queued = n?.claim_status === "queued";
  const [pos, setPos] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [query, setQuery] = useState("");
  const [custom, setCustom] = useState(false);
  const [customVal, setCustomVal] = useState("");
  const [flash, setFlash] = useState(false);
  const [hi, setHi] = useState(0);
  const btn = useRef(null), inp = useRef(null);
  const open = () => {
    const r = btn.current?.getBoundingClientRect(); if (!r) return;
    const W = 284, left = Math.max(8, Math.min(r.left, window.innerWidth - W - 8));
    setErr(""); setQuery(""); setCustom(false); setCustomVal(""); setHi(0);
    setPos(r.bottom + 300 > window.innerHeight && r.top > 320 ? { left, bottom: window.innerHeight - r.top + 6 } : { left, top: r.bottom + 6 });
    setTimeout(() => inp.current?.focus(), 30);
  };
  const close = () => { setPos(null); setTimeout(() => btn.current?.focus(), 0); };
  const list = (tovList || []).filter((t) => t.toLowerCase().includes(query.trim().toLowerCase()));
  const exact = (tovList || []).some((t) => t.toLowerCase() === query.trim().toLowerCase());
  const pick = async (v) => {
    if (!v) return;
    setBusy(true); setErr("");
    try { await onSet(n, v); setFlash(true); setTimeout(() => setFlash(false), 900); setPos(null); }
    catch (e) { setErr(e?.message || "The API rejected that visit type."); }
    setBusy(false);
  };
  const bold = (t) => {
    const i = t.toLowerCase().indexOf(query.trim().toLowerCase());
    if (!query.trim() || i < 0) return t;
    return <>{t.slice(0, i)}<b>{t.slice(i, i + query.trim().length)}</b>{t.slice(i + query.trim().length)}</>;
  };
  const onKey = (e) => {
    if (custom) { if (e.key === "Escape") { e.preventDefault(); setCustom(false); setTimeout(() => inp.current?.focus(), 0); } return; }
    if (e.key === "Escape") { e.preventDefault(); close(); }
    else if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(list.length, h + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(0, h - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); if (hi < list.length) pick(list[hi]); else if (query.trim() && !exact) pick(query.trim()); }
  };
  // revQ-detected value: dotted underline + hover/focus tooltip (same confidence + reason as before).
  const auto = !!tov && n?.tov_auto === true;
  const conf = typeof n?.tov_confidence === "number" ? Math.round(n.tov_confidence * 100) : null;
  const aiTip = `Detected by revQ${conf != null ? ` · ${conf}%` : ""}${n?.tov_reason ? ` — ${n.tov_reason}` : ""}`;
  const val = <span className={auto ? "cc-tovauto" : ""}>{tov || "Not set"}</span>;
  return (
    <div className={`cc-tovc min-w-0 ${auto ? "cc-tip" : ""} ${flash ? "cc-tov-flash" : ""}`} {...(auto && queued ? { tabIndex: 0 } : {})}>
      {queued ? (
        <span className="text-[13px] font-normal leading-[1.4] break-words" style={{ color: tov ? INK : "#AEAEAB" }} data-testid={`cc-tov-readonly-${n.id}`}>{val}</span>
      ) : (
        <button ref={btn} onClick={() => (pos ? close() : open())} aria-haspopup="listbox" aria-expanded={!!pos} title={auto ? undefined : "Set or change the visit type"} aria-description={auto ? aiTip : undefined}
          className="group text-left inline-flex items-center gap-1 text-[13px] rounded-[6px] -ml-1 px-1 max-w-[calc(100%+4px)] py-0.5 transition-colors hover:bg-black/[.05] leading-[1.4] break-words"
          style={{ color: tov ? INK : "#AEAEAB", fontWeight: 400 }} data-testid={tov ? `cc-tov-${n.id}` : `cc-tov-unassigned-${n.id}`}>
          {val} <ChevronDown size={12} className={tov ? "opacity-40 group-hover:opacity-70" : ""} />
        </button>
      )}
      {auto && !pos ? <span className="tt wrap" role="tooltip" data-testid={`cc-tov-ai-tip-${n.id}`}>{aiTip}</span> : null}
      {pos ? createPortal(
        <>
          <div className="fixed inset-0 z-[79]" onClick={close} />
          <div role="listbox" onKeyDown={onKey} className="fixed z-[80] bg-white rounded-[12px] p-1.5 w-[284px]" style={{ ...pos, border: `1px solid ${HAIR}`, boxShadow: "0 16px 44px rgba(0,0,0,.16)" }} data-testid={`cc-tov-menu-${n.id}`}>
            {!custom ? (
              <>
                <div className="text-[10px] font-bold uppercase px-2 pt-1 pb-1" style={{ color: GREY, letterSpacing: ".05em" }}>Type of visit</div>
                <input ref={inp} value={query} onChange={(e) => { setQuery(e.target.value); setHi(0); }} placeholder="Search or type a custom one"
                  className="w-full text-[12.5px] px-2 py-1.5 mb-1 rounded-[8px] outline-none" style={{ border: `1px solid ${HAIR}`, color: INK }} data-testid={`cc-tov-search-${n.id}`} />
                {tovList == null ? (
                  <div className="px-2 py-2 text-[12px]" style={{ color: GREY }}>Loading…</div>
                ) : (
                  <div className="max-h-[220px] overflow-y-auto">
                    {list.map((t, i) => {
                      const cur = t === tov;
                      return (
                        <button key={t} role="option" aria-selected={i === hi} disabled={busy} onMouseEnter={() => setHi(i)} onClick={() => pick(t)}
                          className="w-full text-left text-[12.5px] px-2 py-1.5 rounded-[8px] flex items-center justify-between gap-2"
                          style={{ color: INK, fontWeight: cur ? 700 : 400, background: i === hi ? "rgba(88,86,214,.08)" : "transparent" }}
                          data-testid={`cc-tov-opt-${n.id}-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{bold(t)}{cur ? <span aria-hidden="true" style={{ color: INDIGO }}>✓</span> : null}</button>
                      );
                    })}
                    {!list.length && query.trim() ? <div className="px-2 py-1.5 text-[11.5px]" style={{ color: GREY }}>No match in the list.</div> : null}
                  </div>
                )}
                {err ? <div className="px-2 pt-1 text-[11px] font-semibold" style={{ color: "#B3261E" }} data-testid={`cc-tov-err-${n.id}`}>{err}</div> : null}
                {busy ? <div className="px-2 pt-1 text-[11px]" style={{ color: GREY }}>Saving…</div> : null}
                <div className="px-1 pt-1.5 mt-1 border-t" style={{ borderColor: HAIR }}>
                  {query.trim() && !exact
                    ? <button onClick={() => pick(query.trim())} disabled={busy} className="w-full text-left text-[12.5px] px-2 py-1.5 rounded-[8px] hover:bg-black/[.04]" style={{ color: INK }} data-testid={`cc-tov-use-${n.id}`}>Use “{query.trim()}”</button>
                    : <button onClick={() => { setCustom(true); setCustomVal(query.trim()); }} className="w-full text-left text-[12.5px] px-2 py-1.5 rounded-[8px] hover:bg-black/[.04]" style={{ color: INK }} data-testid={`cc-tov-custom-${n.id}`}>Custom…</button>}
                </div>
              </>
            ) : (
              <div className="p-1">
                <div className="text-[10px] font-bold uppercase px-1 pt-1 pb-1" style={{ color: GREY, letterSpacing: ".05em" }}>Custom type of visit</div>
                <input autoFocus value={customVal} maxLength={60} onChange={(e) => setCustomVal(e.target.value)}
                  className="w-full text-[12.5px] px-2 py-1.5 rounded-[8px] outline-none" style={{ border: `1px solid ${HAIR}`, color: INK }} data-testid={`cc-tov-custom-input-${n.id}`} />
                {err ? <div className="px-1 pt-1 text-[11px] font-semibold" style={{ color: "#B3261E" }}>{err}</div> : null}
                <div className="flex items-center justify-end gap-2 pt-2">
                  <button onClick={() => { setCustom(false); setTimeout(() => inp.current?.focus(), 0); }} className="text-[12px] font-semibold px-2 py-1" style={{ color: GREY }} data-testid={`cc-tov-custom-back-${n.id}`}>Back</button>
                  <button onClick={() => pick(customVal.trim())} disabled={busy || !customVal.trim()} className="text-[12px] font-bold text-white rounded-[8px] px-3 py-1.5 disabled:opacity-40" style={{ background: INK }} data-testid={`cc-tov-custom-save-${n.id}`}>Save</button>
                </div>
              </div>
            )}
          </div>
        </>, document.body) : null}
    </div>
  );
}

export function ProviderCell({ n }) {
  const name = provOf(n), npi = provNpiOf(n);
  const differs = n?.claim?.rendering_provider?.name && n.provider_name && n.claim.rendering_provider.name !== n.provider_name;
  const br = n?.billing_relationship;
  const showBill = br?.status === "matched" && br.billing_id && br.billing_id !== br.rendering_id;
  const bill = showBill ? (
    <div className="cc-tip min-w-0" tabIndex={0} aria-label={`Billing provider ${displayName(br.billing_name)}, NPI ${br.billing_id}`} data-testid={`cc-billing-${n.id}`}>
      <div className="cc-bl" style={{ color: GREY }} title={displayName(br.billing_name)}>Billing provider · {bindCred(displayName(br.billing_name))}</div>
      <span className="tt" role="tooltip">{displayName(br.billing_name)} · NPI {br.billing_id}</span>
    </div>
  ) : br?.status === "missing" ? (
    <div className="cc-tip min-w-0" tabIndex={0} aria-label={`Billing provider missing. ${br.detail || ""}`} data-testid={`cc-billing-missing-${n.id}`}>
      <div className="cc-bl" style={{ color: "#9A5A00", fontWeight: 500 }}>Billing provider missing</div>
      {br.detail ? <span className="tt wrap" role="tooltip">{br.detail}</span> : null}
    </div>
  ) : null;
  if (!name) return <div className="cc-prov min-w-0"><div style={{ color: GREY }}>—</div>{bill}</div>;
  const rn = displayName(name), copyTxt = showBill ? `${rn} (billing provider: ${displayName(br.billing_name)})` : rn;
  return (
    <div className="cc-prov cc-cpy min-w-0">
      <CopyBtn text={copyTxt} what="provider" testid={`cc-copy-prov-${n.id}`} />
      <div className="cc-tip min-w-0" tabIndex={0} aria-label={`${rn}${npi ? `, NPI ${npi}` : ""}`} data-testid={`cc-prov-${n.id}`}>
        <div className="cc-pv" style={{ color: INK }} title={rn}>{bindCred(rn)}</div>
        <span className="tt" role="tooltip">{npi ? `NPI ${npi}` : "NPI —"}{differs ? ` · Dictated by ${displayName(n.provider_name)}` : ""}</span>
      </div>
      {bill}
    </div>
  );
}

function More({ items, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button onFocus={() => setOpen(true)} onBlur={() => setOpen(false)} className="text-[10.5px] font-semibold rounded-[6px] px-1.5 py-0.5" style={{ background: "#F1F1F4", color: INK }} data-testid={testid}>+{items.length}</button>
      {open ? (
        <div className="absolute left-0 top-[110%] z-30 bg-white rounded-[10px] p-2 min-w-[220px]" style={{ border: `1px solid ${HAIR}`, boxShadow: "0 14px 40px rgba(0,0,0,.14)" }}>
          {items.map((d) => (
            <div key={d.k} className="text-[11.5px] py-0.5 flex items-center gap-2">
              {d.num ? <b style={{ color: GREY }}>{d.num}</b> : null}
              <span style={{ fontFamily: MONO, color: d.inferred ? INDIGO : INK }}>{d.code}</span>
              <span className="truncate" style={{ color: GREY }}>{d.why}</span>
            </div>
          ))}
        </div>
      ) : null}
    </span>
  );
}

export function CodingCell({ e }) {
  const v = e.v, dx = v.dx, cpt = v.lines;
  const K = ({ children }) => <span className="text-[10.5px] font-semibold uppercase w-[24px] flex-none" style={{ color: "#6E6E6B", letterSpacing: ".06em" }}>{children}</span>;
  const copyTxt = `CPT ${cpt.map((l) => l.code).join(", ") || "—"}; ICD-10 ${dx.map((d) => d.code).join(", ") || "—"}`;
  return (
    <div className="cc-cpy flex flex-col gap-[6px] min-w-0" data-testid={`cc-coding-${e.n.id}`}>
      {dx.length || cpt.length ? <CopyBtn text={copyTxt} what="codes" testid={`cc-copy-codes-${e.n.id}`} /> : null}
      <div className="cc-cl">
        <K>Dx</K>
        {dx.slice(0, 3).map((d) => (
          <span key={d.code} className="inline-flex items-baseline" title={d.why}>
            <span className="pt">{d.num}</span>
            <span className="cd" style={{ color: d.inferred ? INDIGO : INK }}>{d.code}</span>
          </span>
        ))}
        {!dx.length ? <span className="text-[11.5px]" style={{ color: "#6E6E6B" }}>—</span> : null}
        {dx.length > 3 ? <More items={dx.slice(3).map((d) => ({ ...d, k: d.code }))} testid={`cc-dx-more-${e.n.id}`} /> : null}
      </div>
      <div className="cc-cl">
        <K>CPT</K>
        {cpt.slice(0, 3).map((l, i) => <span key={i} className="cd" title={l.desc}>{l.code}</span>)}
        {!cpt.length ? <span className="text-[11.5px]" style={{ color: "#6E6E6B" }}>—</span> : null}
        {cpt.length > 3 ? <More items={cpt.slice(3).map((l, i) => ({ k: `${l.code}-${i}`, code: l.code, why: l.desc }))} testid={`cc-cpt-more-${e.n.id}`} /> : null}
      </div>
    </div>
  );
}

// Status label trigger; hover, focus or click opens revQ's read (portaled so the table never clips it)
export function ReadPop({ e, onOpen, onLink, children }) {
  const [pos, setPos] = useState(null);
  const btn = useRef(null), t = useRef(null);
  const { headline, items } = useMemo(() => composeRead(e), [e]);
  const show = () => {
    clearTimeout(t.current);
    const r = btn.current?.getBoundingClientRect(); if (!r) return;
    const W = 340, left = Math.max(8, Math.min(r.left - 8, window.innerWidth - W - 8));
    const below = r.bottom + 6, up = window.innerHeight - r.top + 6;
    setPos(below + 220 > window.innerHeight && r.top > 240 ? { left, bottom: up } : { left, top: below });
  };
  const hide = () => { t.current = setTimeout(() => setPos(null), 120); };
  return (
    <>
      <span ref={btn} role="button" tabIndex={0} className="cc-rtrig" aria-label={`${children}. revQ's read: ${headline}`} aria-expanded={!!pos}
        onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide} onClick={show} onKeyDown={(ev) => { if (ev.key === "Escape") setPos(null); }}
        data-testid={`cc-status-read-${e.n.id}`}>{children}</span>
      {pos ? createPortal(
        <div className="fixed z-[80] bg-white rounded-[12px] p-3 w-[340px]" style={{ ...pos, border: `1px solid ${HAIR}`, boxShadow: "0 16px 44px rgba(0,0,0,.16)" }}
          onMouseEnter={() => clearTimeout(t.current)} onMouseLeave={hide} data-testid={`ccv2-read-pop-${e.n.id}`}>
          <div className="text-[12px] mb-2" style={{ color: INK }}>{headline}</div>
          {items.map((it, i) => (
            <div key={i} className="flex items-center gap-2 text-[12px] py-0.5">
              <span className="w-[6px] h-[6px] rounded-full flex-none" style={{ background: it.tone === "red" ? "#FF3B30" : "#FF9500" }} />
              <span className="flex-1" style={{ color: INK }}>{it.text}</span>
              {it.fix === "link" ? (
                <button className="font-semibold" style={{ color: INDIGO }} onClick={() => { setPos(null); onLink(e.n); }} data-testid={`ccv2-pop-link-${e.n.id}`}>Link insurance</button>
              ) : (
                <button className="font-semibold" style={{ color: INDIGO }} onClick={() => { setPos(null); onOpen(e.n); }}>Open claim ›</button>
              )}
            </div>
          ))}
        </div>, document.body) : null}
    </>
  );
}

// flat views: dot + stage word + short reason
export function statusReason(e) {
  if (e.stage === "blocked") {
    const ks = ["none", "ins", "dos", "ptr"].filter((k) => e.band === k || e.blk.has(k));
    return ks.map((k) => GROUP_META[k].label).join(" · ");
  }
  if (e.stage === "review") {
    const f = reviewFlags(e);
    const ks = ["src", "agesex", "proc"].filter((k) => f[k]);
    return ks.length ? ks.map((k) => GROUP_META[k].label).join(" · ") : "Not yet marked ready";
  }
  if (e.stage === "coding") return e.n.stage_message || "";
  return "";
}

export function StatusCell({ e, sig, onOpen, onLink }) {
  const why = statusReason(e);
  return (
    <div className="cc-stat min-w-0" data-testid={`cc-status-${e.n.id}`}>
      <div className="lbl" style={{ color: INK }}><i style={{ background: STAGE_DOT[e.stage] }} />{e.stage === "generated" ? STAGE_WORD[e.stage] : <ReadPop e={e} onOpen={onOpen} onLink={onLink}>{STAGE_WORD[e.stage]}</ReadPop>}</div>
      {why ? <div className="why" data-testid={`cc-status-why-${e.n.id}`}>{why}</div> : null}
      {sig}
    </div>
  );
}
